/** Development-only browser probes. Never imported by `@orbomb/sim`. */

export interface ClientProbeSnapshot {
  fps: number;
  frameMsP50: number;
  frameMsP95: number;
  heapMb: number | null;
  longTasks: number;
  renderMs: number;
  messagesPerSec: number;
  webglLost: number;
  lastError: string | null;
}

const FRAME_SAMPLES = 120;

export function netdiagEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('netdiag');
}

export function attachClientProbe(canvas: HTMLCanvasElement): {
  markFrame: (dtMs: number, renderMs: number) => void;
  noteMessage: () => void;
  snapshot: () => ClientProbeSnapshot;
  dispose: () => void;
} {
  const frames: number[] = [];
  let renderMs = 0;
  let longTasks = 0;
  let webglLost = 0;
  let lastError: string | null = null;
  const messageTimes: number[] = [];
  let observer: PerformanceObserver | null = null;

  const onLost = () => {
    // Do not preventDefault: that pauses restoration and leaves a black canvas.
    webglLost += 1;
    lastError = 'webglcontextlost';
  };
  const onRestored = () => {
    lastError = 'webglcontextrestored';
  };
  const onError = (event: ErrorEvent) => {
    lastError = event.message || 'error';
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    lastError = event.reason instanceof Error ? event.reason.message : 'unhandledrejection';
  };

  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);
  if (typeof window !== 'undefined') {
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
  }

  if (typeof PerformanceObserver !== 'undefined') {
    try {
      observer = new PerformanceObserver((list) => {
        longTasks += list.getEntries().length;
      });
      observer.observe({ type: 'longtask', buffered: true });
    } catch {
      observer = null;
    }
  }

  return {
    markFrame(dtMs: number, nextRenderMs: number) {
      frames.push(Math.max(0, dtMs));
      if (frames.length > FRAME_SAMPLES) frames.shift();
      renderMs = nextRenderMs;
    },
    noteMessage() {
      const now = performance.now();
      messageTimes.push(now);
      while (messageTimes.length > 0 && now - messageTimes[0]! > 1000) messageTimes.shift();
    },
    snapshot() {
      const sorted = [...frames].sort((a, b) => a - b);
      const p = (q: number) => {
        if (sorted.length === 0) return 0;
        return sorted[Math.min(sorted.length - 1, Math.floor(q * (sorted.length - 1)))]!;
      };
      const mean = sorted.length
        ? sorted.reduce((sum, value) => sum + value, 0) / sorted.length
        : 16.67;
      const memory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
      return {
        fps: mean > 0 ? 1000 / mean : 0,
        frameMsP50: p(0.5),
        frameMsP95: p(0.95),
        heapMb: memory ? memory.usedJSHeapSize / (1024 * 1024) : null,
        longTasks,
        renderMs,
        messagesPerSec: messageTimes.length,
        webglLost,
        lastError,
      };
    },
    dispose() {
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      if (typeof window !== 'undefined') {
        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onRejection);
      }
      observer?.disconnect();
    },
  };
}

export function formatClientProbe(probe: ClientProbeSnapshot): string {
  const heap = probe.heapMb === null ? '—' : `${probe.heapMb.toFixed(0)}MB`;
  return `fps ${probe.fps.toFixed(0)}  p95 ${probe.frameMsP95.toFixed(1)}ms  heap ${heap}  long ${probe.longTasks}  gl ${probe.webglLost}`;
}
